/** Every upload the service has accepted, and what an admin may do about one.
 *
 * The contributor's own view of an upload is one row on their profile and a
 * public summary of the issue that held it. This is the other side: every
 * account's uploads in one table, narrowed by what the operations guide says
 * a person arrives with — a status ("what is held?"), a track, an account id
 * from a support message, a source id from a logger's status line. The
 * filters live in the address bar so the Dashboard's "held" figure can link
 * straight to the held rows, and so a row can be sent to somebody.
 *
 * The buttons on a row are drawn from the statuses the service says the row
 * may move to, never from a table written here. `merged` offers nothing — the
 * survey is in the track data and a pull request says so — and nothing moves
 * *to* merged from this screen; only the job may report a merge. Rejecting
 * asks for a note before it sends anything, because the service refuses a
 * rejection without one and the contributor reads what it says.
 *
 * A row acted on stays where it is with its new state, even when it no longer
 * matches the filter: a table that swallows the row you just changed leaves
 * you unsure whether the change happened.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminUploads, setUploadStatus, uploadBundleUrl } from '@/lib/api';
import { bytes, DASH, dateTime, relativeTime } from '@/lib/format';
import type { AdminUpload, AdminUploadPage, UploadStatus } from '@/lib/types';
import {
  EmptyState,
  ErrorNote,
  LoadingPanel,
  Page,
  Segmented,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tag,
  TextField,
  uploadStatusTone,
} from '@/components/ui';
import {
  AdminHeader,
  LoadMore,
  Notice,
  SeverityDot,
  UPLOAD_STATUSES,
  invalidateAdminPulse,
  isUploadStatus,
  sizeSummary,
  transitionConsequence,
  transitionIsDestructive,
  transitionLabel,
  transitionNeedsReason,
  uploadsEmptyTitle,
  usePaged,
  useQueryParams,
} from '@/components/admin';

type StatusFilter = UploadStatus | 'all';

/** A move that has been chosen but not yet confirmed. */
interface Move {
  id: string;
  to: UploadStatus;
}

const MONO_SMALL = {
  display: 'block',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  color: 'var(--color-neutral-600)',
} as const;

const FILTER_INPUT = { minHeight: 30, fontSize: 12.5 } as const;

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export default function AdminUploads() {
  const [search, setParam] = useQueryParams();
  const rawStatus = search.get('status') ?? 'all';
  const status: StatusFilter = isUploadStatus(rawStatus) ? rawStatus : 'all';
  const officialId = search.get('official_id') ?? '';
  const userId = search.get('user_id') ?? '';
  const sourceId = search.get('source_id') ?? '';
  const narrowed = Boolean(officialId || userId || sourceId);

  // Typed into local state and applied on submit, so a half-typed id does
  // not fire a request per keystroke.
  const [draft, setDraft] = useState({ official_id: officialId, user_id: userId, source_id: sourceId });
  useEffect(() => {
    setDraft({ official_id: officialId, user_id: userId, source_id: sourceId });
  }, [officialId, userId, sourceId]);

  const fetcher = useCallback(
    (cursor: string | undefined) =>
      adminUploads({
        status: status === 'all' ? undefined : status,
        official_id: officialId || undefined,
        user_id: userId || undefined,
        source_id: sourceId || undefined,
        cursor,
        limit: 50,
      }),
    [status, officialId, userId, sourceId],
  );
  const listing = usePaged<AdminUpload, AdminUploadPage>(fetcher);

  const [move, setMove] = useState<Move | null>(null);
  const [reason, setReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [moveError, setMoveError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const reasonField = useRef<HTMLTextAreaElement | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const begin = (upload: AdminUpload, to: UploadStatus) => {
    setMove({ id: upload.id, to });
    setReason('');
    setReasonMissing(false);
    setMoveError(null);
  };

  const cancel = () => {
    setMove(null);
    setMoveError(null);
  };

  const apply = async (upload: AdminUpload, to: UploadStatus) => {
    const note = reason.trim();
    if (transitionNeedsReason(to) && !note) {
      setReasonMissing(true);
      reasonField.current?.focus();
      return;
    }
    setBusy(true);
    setMoveError(null);
    try {
      const changed = await setUploadStatus(
        upload.id,
        note ? { status: to, reason: note } : { status: to },
      );
      if (!alive.current) return;
      listing.patch((items) =>
        items.map((row) =>
          row.id === changed.id
            ? {
                ...row,
                status: changed.status,
                status_reason: changed.status_reason,
                status_at: changed.status_at,
                allowed_next: changed.allowed_next,
              }
            : row,
        ),
      );
      setNotice(
        `${upload.track_name || upload.official_id} · ${upload.id}: ${upload.status} → ${changed.status}`,
      );
      setMove(null);
      // The Dashboard's pending and held figures moved with it.
      invalidateAdminPulse();
    } catch (thrown) {
      if (alive.current) setMoveError(thrown);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const submitFilters = (event: FormEvent) => {
    event.preventDefault();
    setParam({
      official_id: draft.official_id.trim(),
      user_id: draft.user_id.trim(),
      source_id: draft.source_id.trim(),
    });
  };

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    ...UPLOAD_STATUSES.map((value) => ({ value, label: capitalise(value) })),
  ];

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader />

      <section aria-label="Uploads" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <Segmented<StatusFilter>
            label="filter by status"
            value={status}
            options={statusOptions}
            onChange={(next) => setParam({ status: next === 'all' ? null : next })}
          />

          <form
            onSubmit={submitFilters}
            style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginLeft: 'auto' }}
          >
            <TextField
              label="track"
              placeholder="official_id"
              value={draft.official_id}
              onChange={(event) => setDraft({ ...draft, official_id: event.currentTarget.value })}
              style={{ ...FILTER_INPUT, width: 170, fontFamily: 'var(--font-mono)' }}
            />
            <TextField
              label="account"
              placeholder="usr_…"
              value={draft.user_id}
              onChange={(event) => setDraft({ ...draft, user_id: event.currentTarget.value })}
              style={{ ...FILTER_INPUT, width: 150, fontFamily: 'var(--font-mono)' }}
            />
            <TextField
              label="installation"
              placeholder="source id"
              value={draft.source_id}
              onChange={(event) => setDraft({ ...draft, source_id: event.currentTarget.value })}
              style={{ ...FILTER_INPUT, width: 130, fontFamily: 'var(--font-mono)' }}
            />
            <button type="submit" className="btn btn-secondary" style={{ fontSize: 12 }}>
              Filter
            </button>
            {narrowed ? (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
                onClick={() => setParam({ official_id: null, user_id: null, source_id: null })}
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>

        {notice ? (
          <Notice tone="ok" onDismiss={() => setNotice(null)}>
            {notice}
          </Notice>
        ) : null}

        {listing.error ? (
          <ErrorNote error={listing.error} title="the uploads could not be read" onRetry={listing.reload} />
        ) : null}

        {listing.loading ? (
          <LoadingPanel label="loading uploads" />
        ) : listing.items.length === 0 ? (
          <EmptyState title={uploadsEmptyTitle(status === 'all' ? null : status, narrowed)}>
            {narrowed
              ? 'Clear a filter to widen the search.'
              : status === 'all'
                ? 'A logger with tracks:write sends a bundle on autosave; the first one lands here.'
                : 'The job decides what becomes of a pending upload on its nightly run.'}
          </EmptyState>
        ) : (
          <Table scroll label="Uploads" style={{ fontSize: 13 }}>
            <THead>
              <TR>
                <TH>Track</TH>
                <TH>Contributor</TH>
                <TH>Received</TH>
                <TH>Survey</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {listing.items.map((row) => {
                const open = move !== null && move.id === row.id;
                return (
                  <Fragment key={row.id}>
                    <TR>
                      <TD nowrap title={row.official_id}>
                        <span style={{ display: 'block' }}>{row.track_name || row.official_id}</span>
                        <span style={MONO_SMALL}>{row.official_id}</span>
                      </TD>
                      <TD nowrap>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Link
                            to={`/admin/users?q=${encodeURIComponent(row.user.id)}`}
                            style={{ color: 'inherit', textDecoration: 'none' }}
                          >
                            {row.user.display_name}
                          </Link>
                          {row.user.disabled ? <Tag tone="danger">disabled</Tag> : null}
                        </span>
                        <span style={MONO_SMALL}>{row.source_id}</span>
                      </TD>
                      <TD nowrap muted title={dateTime(row.received_at)}>
                        {relativeTime(row.received_at)}
                      </TD>
                      <TD nowrap muted>
                        <span style={{ display: 'block' }}>{sizeSummary(row)}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--color-neutral-600)' }}>
                          {bytes(row.bytes)} · v{row.version}
                        </span>
                      </TD>
                      <TD>
                        <Tag tone={uploadStatusTone(row.status)}>{row.status}</Tag>
                        {row.status_reason ? (
                          <span
                            style={{
                              display: 'block',
                              marginTop: 3,
                              fontSize: 11.5,
                              color: 'var(--color-neutral-400)',
                              textWrap: 'pretty',
                            }}
                          >
                            {row.status_reason}
                          </span>
                        ) : null}
                        {row.issue ? (
                          <Link
                            to={`/admin/issues?status=${row.issue.status}&issue=${encodeURIComponent(row.issue.id)}`}
                            title={row.issue.summary}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              marginTop: 3,
                              fontSize: 11.5,
                              color: 'var(--color-neutral-400)',
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <SeverityDot severity={row.issue.severity} size={6} />
                            <code style={{ fontSize: 11 }}>{row.issue.type}</code>
                            <span>· {row.issue.status}</span>
                          </Link>
                        ) : null}
                        {row.pr_url ? (
                          <a
                            href={row.pr_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{ display: 'block', marginTop: 3, fontSize: 11.5 }}
                          >
                            pull request
                          </a>
                        ) : null}
                      </TD>
                      <TD nowrap>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          {row.allowed_next.map((to) => (
                            <button
                              key={to}
                              type="button"
                              className={`btn ${transitionIsDestructive(to) ? 'btn-danger' : 'btn-secondary'}`}
                              style={{ fontSize: 11.5 }}
                              disabled={busy}
                              aria-pressed={open && move.to === to}
                              onClick={() => begin(row, to)}
                            >
                              {transitionLabel(row.status, to)}
                            </button>
                          ))}
                          {row.has_bundle ? (
                            <a
                              className="btn btn-ghost"
                              style={{ fontSize: 11.5 }}
                              href={uploadBundleUrl(row.id)}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              raw bundle
                            </a>
                          ) : null}
                          {row.allowed_next.length === 0 && !row.has_bundle ? DASH : null}
                        </span>
                      </TD>
                    </TR>

                    {open && move ? (
                      <TR>
                        <TD colSpan={6}>
                          <form
                            style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '2px 0 8px' }}
                            onSubmit={(event) => {
                              event.preventDefault();
                              void apply(row, move.to);
                            }}
                          >
                            <div className="field">
                              <label htmlFor={`move-${row.id}`}>
                                {transitionLabel(row.status, move.to).toLowerCase()}{' '}
                                <span style={{ fontFamily: 'var(--font-mono)' }}>{row.id}</span> · note{' '}
                                <span style={{ color: 'var(--color-neutral-600)' }}>
                                  {transitionNeedsReason(move.to)
                                    ? '— required; the contributor reads it'
                                    : '— optional; kept with the upload'}
                                </span>
                              </label>
                              <textarea
                                id={`move-${row.id}`}
                                ref={reasonField}
                                className="input"
                                rows={2}
                                value={reason}
                                // The field did not exist a moment ago; the click
                                // that revealed it was a request to type in it.
                                autoFocus
                                aria-invalid={reasonMissing || undefined}
                                style={{
                                  minHeight: 52,
                                  fontSize: 13,
                                  borderColor: reasonMissing ? 'var(--color-error)' : undefined,
                                }}
                                placeholder="what you decided, and why"
                                onChange={(event) => {
                                  setReason(event.target.value);
                                  if (event.target.value.trim()) setReasonMissing(false);
                                }}
                              />
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 11.5,
                                  color: reasonMissing ? 'var(--color-error)' : 'var(--color-neutral-600)',
                                  textWrap: 'pretty',
                                }}
                              >
                                {reasonMissing
                                  ? 'rejecting needs a note — the contributor is told what it says.'
                                  : transitionConsequence(move.to)}
                              </div>
                            </div>

                            {moveError ? (
                              <ErrorNote error={moveError} title="the upload was not moved" compact />
                            ) : null}

                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                type="submit"
                                className={`btn ${transitionIsDestructive(move.to) ? 'btn-danger' : 'btn-primary'}`}
                                style={{ fontSize: 12 }}
                                disabled={busy}
                              >
                                {busy ? 'Applying…' : `${transitionLabel(row.status, move.to)} — confirm`}
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
                                disabled={busy}
                                onClick={cancel}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        </TD>
                      </TR>
                    ) : null}
                  </Fragment>
                );
              })}
            </TBody>
          </Table>
        )}

        <LoadMore listing={listing} />
      </section>
    </Page>
  );
}
